import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, Truck, ArrowLeft, ExternalLink } from "lucide-react";
import { api, money } from "../lib/api";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const Step = ({ icon: Icon, label, date, done, testid }) => (
  <div data-testid={testid} className="flex-1 flex flex-col items-center text-center">
    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${done ? "bg-[#0D9488] text-white" : "bg-[#EFECE5] text-slate-400"}`}><Icon className="w-5 h-5" /></div>
    <div className={`mt-2 text-sm font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{label}</div>
    <div className="text-xs text-slate-500">{date}</div>
  </div>
);

export default function OrderPage() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setMissing(true)); }, [id]);

  if (missing) return <div className="max-w-xl mx-auto py-24 text-center"><p>We couldn't find that order.</p><Link to="/dashboard" className="text-[#0D9488] font-semibold">Back to dashboard</Link></div>;
  if (!o) return <div className="py-24 text-center text-slate-500">Loading…</div>;
  const shipped = o.fulfillment_status === "shipped";
  const addr = o.shipping?.address;
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="order-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0D9488]"><ArrowLeft className="w-4 h-4" />My dashboard</Link>
      <h1 className="font-display text-4xl font-semibold mt-4">Order #{o.id.slice(0, 8).toUpperCase()}</h1>
      <p className="text-slate-600 mt-1">Placed {fmt(o.created_at)}</p>
      {o.gift && <div data-testid="order-gift-info" className="mt-4 rounded-xl bg-[#0D9488]/10 border border-[#0D9488]/30 p-4 text-sm">Gift for <b>{o.gift.recipient_name}</b>{o.gift.message ? ` — “${o.gift.message}”` : ""}</div>}

      <div className="mt-8 bg-white border border-[#E4DFD4] rounded-2xl p-6 sm:p-8">
        <div className="flex items-start">
          <Step icon={CheckCircle2} label="Paid" date={fmt(o.paid_at || o.created_at)} done testid="order-step-paid" />
          <div className="flex-1 h-0.5 mt-5 bg-[#0D9488]" />
          <Step icon={Package} label="Preparing" date={shipped ? "" : "In progress"} done testid="order-step-preparing" />
          <div className={`flex-1 h-0.5 mt-5 ${shipped ? "bg-[#0D9488]" : "bg-[#E4DFD4]"}`} />
          <Step icon={Truck} label="Shipped" date={fmt(o.shipped_at)} done={shipped} testid="order-step-shipped" />
        </div>
        {shipped ? (
          <div data-testid="order-tracking" className="mt-8 rounded-xl bg-[#0D9488]/10 border border-[#0D9488]/30 p-5 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm"><div className="text-slate-600">{o.carrier} tracking number</div><div className="font-mono font-bold text-base">{o.tracking_number}</div></div>
            {o.tracking_url && <a data-testid="order-track-link" href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-5 py-2.5 text-sm font-semibold transition-colors">Track with {o.carrier}<ExternalLink className="w-4 h-4" /></a>}
          </div>
        ) : (
          <p data-testid="order-preparing-msg" className="mt-8 text-sm text-slate-600">We're preparing your order. You'll get an email with tracking as soon as it ships.</p>
        )}
      </div>

      <div className="mt-6 bg-white border border-[#E4DFD4] rounded-2xl p-6">
        <h2 className="font-display text-xl">Items</h2>
        <div className="mt-3 divide-y divide-[#E4DFD4]">
          {o.items.map((i) => (
            <div key={i.product_id} className="flex justify-between py-3 text-sm"><span>{i.name} × {i.quantity}</span><span className="font-mono">{money(i.price * i.quantity)}</span></div>
          ))}
        </div>
        <div className="flex justify-between pt-3 font-semibold"><span>Total</span><span data-testid="order-total" className="font-mono">{money(o.amount_total)}</span></div>
        {addr && <div className="mt-5 text-sm text-slate-600"><div className="field-label">Shipping to</div>{o.shipping?.name}<br />{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />{addr.city}, {addr.state} {addr.postal_code}</div>}
      </div>
    </div>
  );
}
